import useSWRMutation from "swr/mutation";
import { useState } from "react";
import { favouriteAction, favouriteActionString, getAuthHeader } from "@/src/service/apiService"
import { useToast } from "@/src/hooks/use-toast";
import { Button } from "../ui/button";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useRouter } from 'nextjs-toploader/app';

export default function FavoriteButton({ productId, initialFavorite }: { productId: string; initialFavorite: boolean }) {
    const [isFavorite, setIsFavorite] = useState(initialFavorite);
    const router = useRouter();
    const { toast } = useToast();

    const key = favouriteActionString();
    const { trigger, isMutating } = useSWRMutation(
        key,
        (url, { arg }: { arg: { productId: string } }) => favouriteAction(arg),
        {
            optimisticData: (data: any, variables: any) => ({
                ...data,
            }),
            onError(err: any) {
                console.log("Favorite API Error:", err);

                if (err?.response?.data?.message?.includes("Token not found")) {
                    toast({
                        className: "bg-main-100",
                        description: "Sign in required!!",
                    });
                    // router.push("/u/sign-in");
                } else {
                    toast({
                        className: "bg-red-500 text-white",
                        description: "Something went wrong. Please try again.",
                    });
                }

                // Revert the optimistic UI update on actual failure
                setIsFavorite((prev) => !prev);
            },
            onSuccess(data) {
                console.log("Favorite Updated Successfully:", data?.data);

                if (data?.data?.data === "DELETED") {
                    setIsFavorite((prev) => false);
                } else {
                    setIsFavorite((prev) => true);
                }
            },
        }
    );

    const handleFavoriteClick = async () => {
        if (!productId) {
            toast({
                className: "bg-red-500 text-white",
                description: "Invalid product ID",
            });
            return;
        }

        try {
            setIsFavorite((prev) => !prev); // ✅ Toggle UI optimistically
            await trigger({ productId });
        } catch (err: any) {
            // console.error("Trigger Error:", err);
        }
    };

    return (
        <Button onClick={handleFavoriteClick} variant="ghost" disabled={isMutating} className="relative"
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
            <motion.div
                key={isFavorite ? "liked" : "unliked"}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
            >
                <Heart
                    className="w-6 h-6"
                    fill={isFavorite ? "red" : "transparent"}
                    color={isFavorite ? "red" : "gray"}
                />
            </motion.div>
        </Button>
    );
}
